import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { briefKey, type AdminLabel, type BriefListItem } from "@/lib/briefs";

export const LABELS: AdminLabel[] = ["Noted", "In discussion", "In build", "Live", "Parked"];

// --- Review (admins) -------------------------------------------------------------

export interface Submission {
  id: string;
  title: string;
  summary: string | null;
  ownerId: string;
  ownerName: string | null;
  ownerEmail: string;
  submissionCount: number;
  lastSubmittedAt: string | null;
  editedSinceSubmit: boolean;
  adminLabel: AdminLabel | null;
  adminRemark: string | null;
  adminAt: string | null;
  archivedAt: string | null;
}

export interface LabelCounts {
  all: number;
  unlabelled: number;
  byLabel: Record<AdminLabel, number>;
}

export function useSubmissions(filter: { label: string; q: string }) {
  const params = new URLSearchParams();
  if (filter.label) params.set("label", filter.label);
  if (filter.q) params.set("q", filter.q);
  return useQuery({
    queryKey: ["submissions", filter.label, filter.q],
    queryFn: () => api<{ submissions: Submission[]; unlabelled: number; counts: LabelCounts }>(`/review/submissions?${params}`),
    // Keep the pills' counts on screen while the next filter loads.
    placeholderData: (prev) => prev,
  });
}

export function useLabel(briefId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { adminLabel?: AdminLabel | null; adminRemark?: string | null }) =>
      api(`/review/${briefId}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: briefKey(briefId) });
      void qc.invalidateQueries({ queryKey: ["submissions"] });
    },
  });
}

// --- Superadmin ------------------------------------------------------------------

export interface UserUsage {
  id: string;
  email: string;
  name: string | null;
  role: "superadmin" | "admin" | "employee" | null;
  deactivated: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  briefs: { drafts: number; submitted: number; archived: number };
  ai: { turns: number; calls: number; inputTokens: number; outputTokens: number; costUsd: number };
}

export function useUsage() {
  return useQuery({ queryKey: ["admin", "usage"], queryFn: () => api<{ users: UserUsage[] }>("/admin/usage").then((r) => r.users) });
}

export function useAllBriefs() {
  return useQuery({
    queryKey: ["admin", "briefs"],
    queryFn: () => api<{ briefs: Array<BriefListItem & { ownerId: string }> }>("/admin/briefs").then((r) => r.briefs),
  });
}

export function useUserActions() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin"] });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: "admin" | "employee" }) => api(`/admin/users/${id}/role`, { method: "PATCH", body: { role } }),
    onSuccess: invalidate,
  });
  const setDeactivated = useMutation({
    mutationFn: ({ id, deactivated }: { id: string; deactivated: boolean }) =>
      api(`/admin/users/${id}/deactivated`, { method: "PATCH", body: { deactivated } }),
    onSuccess: invalidate,
  });
  return { setRole, setDeactivated };
}

export interface AiTab {
  enabled: boolean;
  baseUrl: string;
  windowDays: number;
  models: Array<{ role: string; model: string; fallbackModel: string | null; think: boolean }>;
  metrics: Array<{
    model: string;
    role: string;
    calls: number;
    ok: number;
    successRate: number;
    toolCalls: number;
    toolCallsValid: number;
    toolValidity: number | null;
    invalidJson: number;
    jsonValidity: number | null;
    p50Ms: number | null;
    p95Ms: number | null;
    firstTokenP50Ms: number | null;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  }>;
  acceptance: Array<{ model: string; writes: number; kept: number; edited: number; undone: number; rate: number | null }>;
  byStatus: Array<{ status: string; n: number }>;
  spendTodayUsd: number;
  limits: Array<{ key: string; value: number; default: number }>;
  circuits: Array<{ model: string; failures: number; openUntil: number }>;
  gate: { running: { interactive: number; background: number }; queued: { interactive: number; background: number } };
}

export function useAiTab(days: number) {
  return useQuery({ queryKey: ["admin", "ai", days], queryFn: () => api<AiTab>(`/admin/ai?days=${days}`), refetchInterval: 30_000 });
}

export function useCatalog() {
  return useQuery({
    queryKey: ["admin", "ai", "catalog"],
    queryFn: () => api<{ models: string[] }>("/admin/ai/catalog").then((r) => r.models),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export interface CanaryResult {
  ok: boolean;
  checks: Array<{ name: string; ok: boolean; ms: number; detail: string }>;
}

export function useAiActions() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin", "ai"] });
  const setModel = useMutation({
    mutationFn: ({ role, ...body }: { role: string; model: string; fallbackModel?: string | null; think?: boolean; force?: boolean }) =>
      api<{ ok: boolean; canary: CanaryResult | null; message?: string }>(`/admin/ai/models/${role}`, { method: "PATCH", body }),
    onSuccess: invalidate,
  });
  const canary = useMutation({
    mutationFn: (body: { model: string; role: string; think?: boolean }) => api<CanaryResult>("/admin/ai/canary", { method: "POST", body }),
  });
  const setLimit = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number }) => api(`/admin/ai/limits/${key}`, { method: "PATCH", body: { value } }),
    onSuccess: invalidate,
  });
  return { setModel, canary, setLimit };
}

export interface KnownDataItem {
  id: string;
  appSlug: string;
  appName: string;
  holds: string;
  enabled: boolean;
  updatedAt: string;
}

export function useKnownData() {
  return useQuery({ queryKey: ["admin", "known-data"], queryFn: () => api<{ items: KnownDataItem[] }>("/admin/known-data").then((r) => r.items) });
}

export function useKnownDataActions() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin", "known-data"] });
  const create = useMutation({
    mutationFn: (body: { appSlug: string; appName: string; holds: string; enabled?: boolean }) => api("/admin/known-data", { method: "POST", body }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<Omit<KnownDataItem, "id" | "updatedAt">>) =>
      api(`/admin/known-data/${id}`, { method: "PATCH", body: patch }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/admin/known-data/${id}`, { method: "DELETE" }), onSuccess: invalidate });
  return { create, update, remove };
}

export interface AuditEntry {
  id: string;
  actorId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  detail: Record<string, unknown> | null;
  at: string;
}

export function useAudit() {
  return useQuery({ queryKey: ["admin", "audit"], queryFn: () => api<{ entries: AuditEntry[] }>("/admin/audit").then((r) => r.entries) });
}

// --- Daily digest ----------------------------------------------------------------

export interface DigestRun {
  day: string;
  status: "running" | "sent" | "failed";
  attempts: number;
  startedAt: string;
  finishedAt: string | null;
  verdict: "ok" | "warn" | "fail" | null;
  subject: string | null;
  recipients: string | null;
  error: string | null;
  headline: {
    people: number;
    newPeople: number;
    briefsStarted: number;
    submissions: number;
    chatTurns: number;
    tokens: number;
    costUsd: number;
    checks: { ok: number; warn: number; fail: number };
  } | null;
}

export interface DigestSettings {
  recipients: string[];
  enabled: boolean;
  problems: string[];
  schedule: string;
  maxAttempts: number;
}

export function useDigest() {
  return useQuery({
    queryKey: ["admin", "digest"],
    queryFn: () => api<{ settings: DigestSettings; runs: DigestRun[] }>("/admin/digest"),
  });
}

export function useDigestActions() {
  const qc = useQueryClient();
  const preview = useMutation({
    mutationFn: (probe: boolean) => api<{ subject: string; html: string; text: string }>(`/admin/digest/preview${probe ? "?probe=1" : ""}`),
  });
  const send = useMutation({
    mutationFn: () => api<{ ok: true; subject: string }>("/admin/digest/send", { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "audit"] }),
  });
  return { preview, send };
}
